import { useMutation, UseMutationResult } from '@tanstack/react-query'

import useSessionStore from '@/stores/useSessionStore'
import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

export interface UpdateUserPasswordResult {
  error: Error | null
  success: boolean
}

export interface UpdateUserPasswordInput {
  currentPassword: string
  newPassword: string
  newPasswordConfirmation: string
}

export const useUpdateUserPasswordsApi = (): UseMutationResult<
  UpdateUserPasswordResult,
  Error,
  UpdateUserPasswordInput
> => {
  const authToken = useSessionStore((store) => store.authToken)
  const url = getV5ApiUrl('/users/passwords')

  const mutationFn = (input: UpdateUserPasswordInput): Promise<UpdateUserPasswordResult> =>
    fetch(url, {
      method: 'PUT',
      headers: {
        'Content-type': 'application/json',
        'x-brand-id': brand.key,
        'x-user-token': authToken || '',
      },
      body: JSON.stringify({
        current_password: input.currentPassword,
        password: input.newPassword,
        password_confirmation: input.newPasswordConfirmation,
        reset_password_token: '',
      }),
    }).then((response) =>
      response.ok
        ? response
            .json()
            .then((data: { success: boolean }) =>
              !data.success
                ? Promise.reject(new Error('Failed to update password'))
                : { error: null, success: true },
            )
        : response
            .json()
            .then((data: { error: string }) =>
              Promise.reject(new Error(data.error || 'Failed to update password')),
            ),
    )

  return useMutation<UpdateUserPasswordResult, Error, UpdateUserPasswordInput>({
    mutationFn,
  })
}
